import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Game } from '@/lib/types';
import GameSetupForm from '@/components/GameSetupForm';
import { LoadingSpinner, EmptyState } from '@/components/ui';

export default function EditGamePage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchGame() {
      if (!gameId) return;
      try {
        const docRef = doc(db, 'games', gameId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setGame({ ...docSnap.data(), id: docRef.id } as Game);
        } else {
          setError('Game not found');
        }
      } catch (err) {
        setError('Failed to fetch game details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchGame();
  }, [gameId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f7f8] flex items-center justify-center">
        <LoadingSpinner message="Loading Game Details..." />
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-[#f5f7f8] flex items-center justify-center">
        <EmptyState 
          icon="❌" 
          title={error || "Game Not Found"} 
          description="The requested game session could not be found."
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7f8] py-6 px-4">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate('/setup')}
          className="mb-4 text-sm font-medium text-[#006855] hover:text-[#006855]/80 transition-colors"
        >
          ← Back to Setup
        </button>
        <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
          <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">Game Setup</div>
          <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Edit Session</h1>
        </div>
        <GameSetupForm mode="edit" initialData={game} />
      </div>
    </div>
  );
}
